import { useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Grip, RotateCcw, Settings2, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDataQuery } from "@/hooks/useDataQuery";

export function orderedWidgets(widgets, order = []) {
  const byId = new Map(widgets.map(widget => [widget.id, widget]));
  return [...new Set([...order, ...widgets.map(widget => widget.id)])].map(id => byId.get(id)).filter(Boolean);
}

export function moveWidget(order, source, target, after = false) {
  if (source === target || !order.includes(source) || !order.includes(target)) return order;
  const next = order.filter(id => id !== source);
  next.splice(next.indexOf(target) + (after ? 1 : 0), 0, source);
  return next;
}

export default function DashboardGrid({ widgets }) {
  const { user } = useAuth();
  const client = useQueryClient();
  const key = ["dashboard-layout", user.id];
  const { data: saved, loading, error, refetch } = useDataQuery(key, () => api.get("/dashboard/layout").then(r => r.data));
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [drag, setDrag] = useState(null);
  const [announcement, setAnnouncement] = useState("");
  const pointer = useRef(null);
  const reduceMotion = useReducedMotion();
  const editing = draft !== null;
  const layout = draft || saved || { version: 1, order: [], hidden: [] };
  const sorted = orderedWidgets(widgets, layout.order);
  const visible = sorted.filter(w => !layout.hidden.includes(w.id));
  const reorder = (source, target, after) => {
    setDraft(current => ({ ...current, order: moveWidget(orderedWidgets(widgets, current.order).map(w => w.id), source, target, after) }));
    setAnnouncement("Posisi widget diubah. Pilih Simpan untuk menyimpan susunan.");
  };
  const moveStep = (id, direction) => {
    const index = visible.findIndex(w => w.id === id);
    const target = visible[index + direction];
    if (target) reorder(id, target.id, direction > 0);
  };
  const finishDrag = event => {
    const current = pointer.current;
    pointer.current = null;
    setDrag(null);
    if (event.type !== "pointercancel" && current?.moved && current.target) reorder(current.id, current.target, current.after);
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/dashboard/layout", draft);
      client.setQueryData(key, data);
      setDraft(null);
      toast.success("Susunan dashboard tersimpan untuk akunmu");
    } catch { toast.error("Susunan belum tersimpan. Coba simpan lagi."); }
    finally { setSaving(false); }
  };
  return <>
    <div className="dw-section-heading">
      <div><p className="dw-eyebrow">RUANG KERJAMU</p><h2>Ringkasan bisnis</h2></div>
      {!editing ? <button className="dw-button" data-testid="dashboard-customize" disabled={loading || !!error || !widgets.length}
        onClick={() => setDraft({ version: 1, order: [...(saved?.order || [])], hidden: [...(saved?.hidden || [])] })}>
        <Settings2 size={16} />Atur dashboard</button> : <span className="dw-edit-badge">Mode pengaturan</span>}
    </div>
    {error && <div className="dw-notice" role="alert">Susunan tersimpan belum bisa dimuat. <button onClick={() => refetch()}>Coba lagi</button></div>}
    {editing && <div className="dw-editor" data-testid="dashboard-layout-editor">
      <div className="dw-editor-top"><div><strong>Sesuaikan dashboardmu</strong><p>Geser lewat ikon titik, atau gunakan tombol panah. Pilih widget yang ingin ditampilkan.</p></div>
        <div className="dw-editor-actions">
          <button className="dw-button" disabled={saving} onClick={() => setDraft({ version: 1, order: [], hidden: [] })}><RotateCcw size={15} />Reset</button>
          <button className="dw-button" disabled={saving} onClick={() => { setDraft(null); setDrag(null); }}><X size={15} />Batal</button>
          <button className="dw-button dw-primary" data-testid="dashboard-layout-save" disabled={saving} onClick={save}><Check size={16} />{saving ? "Menyimpan…" : "Simpan"}</button>
        </div>
      </div>
      <div className="dw-widget-options">{sorted.map(widget => <label key={widget.id}>
        <input type="checkbox" disabled={saving} checked={!layout.hidden.includes(widget.id)}
          onChange={e => setDraft(current => ({ ...current, hidden: e.target.checked ? current.hidden.filter(id => id !== widget.id) : [...current.hidden, widget.id] }))} />{widget.title}
      </label>)}</div>
    </div>}
    <p className="sr-only" role="status" aria-live="polite">{announcement}</p>
    {loading ? <div className="dw-skeleton-grid" role="status" aria-label="Memuat dashboard">{[0, 1, 2, 3].map(i => <div key={i} />)}</div> :
      <div className={`dw-grid ${editing ? "is-editing" : ""}`} data-testid="dashboard-widget-grid">
        {visible.map((widget, index) => <motion.article key={widget.id} layout="position"
          transition={{ layout: { duration: reduceMotion ? 0 : 0.24, ease: [0.22, 1, 0.36, 1] } }}
          initial={reduceMotion ? false : { opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          data-dashboard-widget={widget.id} data-testid={`dashboard-widget-${widget.id}`}
          className={`dw-widget dw-span-${widget.size || 1} ${widget.className || ""} ${drag?.target === widget.id ? "dw-drop-target" : ""} ${drag?.id === widget.id ? "dw-drag-source" : ""}`}>
          {editing && <div className="dw-widget-toolbar">
            <button className="dw-grip" aria-label={`Geser ${widget.title}`} title="Geser, atau Alt + tombol panah" disabled={saving}
              onKeyDown={e => { if (e.altKey && ["ArrowLeft", "ArrowUp", "ArrowRight", "ArrowDown"].includes(e.key)) { e.preventDefault(); moveStep(widget.id, ["ArrowLeft", "ArrowUp"].includes(e.key) ? -1 : 1); } }}
              onPointerDown={e => { if (e.button !== 0) return; e.currentTarget.setPointerCapture(e.pointerId); pointer.current = { id: widget.id, title: widget.title, x: e.clientX, y: e.clientY }; }}
              onPointerMove={e => {
                const current = pointer.current;
                if (!current || current.id !== widget.id) return;
                if (!current.moved && Math.hypot(e.clientX - current.x, e.clientY - current.y) < 6) return;
                current.moved = true;
                const target = document.elementFromPoint(e.clientX, e.clientY)?.closest("[data-dashboard-widget]");
                const rect = target?.getBoundingClientRect();
                current.target = target?.dataset.dashboardWidget;
                current.after = rect && e.clientY > rect.top + rect.height / 2;
                setDrag({ ...current, x: e.clientX, y: e.clientY });
              }} onPointerUp={finishDrag} onPointerCancel={finishDrag} onLostPointerCapture={() => { pointer.current = null; setDrag(null); }}>
              <Grip size={17} /><span>{widget.title}</span>
            </button>
            <button className="dw-icon-button" aria-label={`Majukan ${widget.title}`} title="Pindah lebih awal" disabled={saving || index === 0} onClick={() => moveStep(widget.id, -1)}><ArrowLeft size={15} /></button>
            <button className="dw-icon-button" aria-label={`Mundurkan ${widget.title}`} title="Pindah lebih akhir" disabled={saving || index === visible.length - 1} onClick={() => moveStep(widget.id, 1)}><ArrowRight size={15} /></button>
          </div>}
          <div className="dw-widget-content">{widget.content}</div>
        </motion.article>)}
      </div>}
    {!loading && visible.length === 0 && <div className="dw-empty" data-testid="dashboard-empty-state">
      <Settings2 size={28} /><h3>{widgets.length ? "Widget disembunyikan" : "Belum ada widget yang diaktifkan"}</h3>
      <p>{widgets.length ? "Buka Atur dashboard untuk menampilkan kembali widget pilihanmu." : "Hubungi admin untuk mengatur widget dan hak akses akunmu."}</p>
    </div>}
    {drag && <div className="dw-drag-preview" style={{ left: drag.x + 12, top: drag.y + 12 }} aria-hidden="true"><Grip size={17} />{drag.title}</div>}
  </>;
}
